import {useCallback, useEffect, useState} from 'react';

import {useToast} from '@chakra-ui/react';

import {useQueryClient} from '@tanstack/react-query';
import {SubmitHandler, useForm} from 'react-hook-form';

import {
  Question,
  getFollowUpAnswers,
  getFollowUpQuestions,
  saveFollowUpAnswer,
} from '../../services/forms-v2-service';

export function useFollowup(idSet?: string) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);

  const toast = useToast();
  const form = useForm();
  const queryClient = useQueryClient();
  const {handleSubmit} = form;

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      if (!idSet) {
        return;
      }
      const [questions, answers] = await Promise.all([
        getFollowUpQuestions(idSet),
        getFollowUpAnswers(idSet),
      ]);
      setQuestions(questions);
      for (const it of answers) {
        form.setValue(it.idQuestion, it.rawAnswer);
      }
    })().finally(() => setIsLoading(false));
  }, [form, idSet]);

  const saveAnswers: SubmitHandler<
    Record<string, string | string[] | null | number>
  > = useCallback(
    async data => {
      setIsSaving(true);
      if (!idSet) {
        throw new Error('Undefinied idSet');
      }
      for (const key in data) {
        await saveFollowUpAnswer(idSet, {
          idQuestion: key,
          rawAnswer: data[key],
        });
      }
      setIsSaving(false);
      toast({
        title: 'Respuestas guardadas',
        description: 'Las respuestas se han guardado correctamente',
        position: 'top-right',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await queryClient.invalidateQueries({
        queryKey: ['answers-sets'],
      });
    },
    [idSet, queryClient, toast],
  );

  return {
    isLoading,
    questions,
    form,
    isSaving,
    onSubmit: handleSubmit(saveAnswers),
  };
}
